// src/pages/CaseSelectionPage/index.tsx

import React, { useState, useMemo, useEffect, useContext } from "react";
import { Button } from "../../components/ui/Button";
import { useFetchCases } from "src/hooks/useFetchCases";
import styles from "./index.module.css";
import ModeSwitcher from "./ModeSwitcher";
import FiltersBar from "./FiltersBar";
import CasePromptDialog from "./CasePromptDialog";
import { useAuth } from "src/contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { InterviewContext } from "src/contexts/InterviewContext";
import { useLoading } from "src/contexts/LoadingContext";
import { getTopicStyle } from "src/utils/topicColors";
import ErrorMessage from "src/components/ErrorMessage";
import CreditConfirmationDialog from "./CreditConfirmationDialog";

export interface Topic {
  name: string;
  color?: string;
}

export interface InterviewCase {
  id: number;
  title: string;
  industry: string;
  company: string;
  isInterviewerLed: boolean;
  expectedTime: string;
  topics: Topic[];
  difficulty: "Easy" | "Medium" | "Hard";
  mathFocus?: "High" | "Low";
  wordBoosts: string[];
  caseType: string;
  caseProblemContext: string;
  caseProblem: string;
}

export interface Filters {
  difficulty: string;
  company: string[];
  industryFocus: string;
  businessProblem: string[];
  mathFocus: string;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const SelectInterviewCases: React.FC = () => {
  const { handleTimeBasedLoading } = useLoading();

  const { firebaseUser, userDoc, loading } = useAuth();
  const stableIds = React.useMemo<number[]>(() => [], []);
  const [cases, setCases] = useState<InterviewCase[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [mode, setMode] = useState<"mock" | "practice">("mock");
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<InterviewCase | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    difficulty: "All",
    company: [],
    industryFocus: "All",
    businessProblem: [],
    mathFocus: "All",
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);
  const [pendingCaseId, setPendingCaseId] = useState<number | null>(null);

  const { data, isLoading, isError, error } = useFetchCases(stableIds);

  useEffect(() => {
    if (data && data.length > 0) {
      setCases(data);
    }
  }, [data]);

  const userOngoingInterview = userDoc?.ongoingInterview || [];
  const userFinishedInterview = userDoc?.finishedInterview || [];

  const filteredCases = useMemo(() => {
    return cases.filter((c) => {
      if (
        filters.difficulty !== "All" &&
        c.difficulty.toLowerCase() !== filters.difficulty.toLowerCase()
      )
        return false;

      if (
        filters.industryFocus !== "All" &&
        c.industry.toLowerCase() !== filters.industryFocus.toLowerCase()
      )
        return false;
      if (filters.mathFocus !== "All" && c.mathFocus !== filters.mathFocus)
        return false;

      if (filters.company.length > 0 && !filters.company.includes(c.company))
        return false;

      if (
        filters.businessProblem.length > 0 &&
        !filters.businessProblem.includes(c.caseType)
      )
        return false;

      return true;
    });
  }, [cases, filters]);

  const { startInterviewHandler, settIsEndHandler, checkInterviewCredits } =
    useContext(InterviewContext)!;
  const navigate = usePartnerAwareNavigate();

  const handleStartMockInterview = async (caseId: number) => {
    setErrorMessage(null);
    console.log("Starting interview for case:", caseId);
    try {
      // First check if user has enough credits before showing the confirmation dialog
      const hasCredits = await checkInterviewCredits(firebaseUser?.uid ?? "");

      if (!hasCredits) {
        setErrorMessage(
          "You don't have enough interview credits. Purchase more to continue."
        );
        return;
      }

      // Show confirmation dialog
      setPendingCaseId(caseId);
      setShowCreditConfirmation(true);
    } catch (err) {
      console.error("Error checking credits:", err);
      setErrorMessage(
        "An error occurred while checking your credits. Please try again."
      );
    }
  };

  const handleConfirmStart = async () => {
    if (!pendingCaseId) return;

    try {
      // If they have credits, continue with loading and starting the interview
      const result = await handleTimeBasedLoading(
        startInterviewHandler(
          firebaseUser?.uid ?? "",
          pendingCaseId,
          false,
          false,
          ""
        ),
        {
          totalDuration: 3500,
          messages: [
            "Initializing your mock interview...",
            "Setting up your case materials...",
            "Preparing interview environment...",
            "Almost ready to begin...",
          ],
          onComplete: () => {
            console.log("Loading complete");
          },
        }
      );
      console.log("Interview start result:", result);

      // Check if result is an error object
      if (typeof result === "object" && "error" in result) {
        setErrorMessage(result.error);
        return;
      }

      if (result && typeof result === "string" && result.length > 0) {
        const matchingCase = cases.find((c) => c.id === pendingCaseId);
        console.log("Interview started successfully!");
        settIsEndHandler(false);
        navigate(`/mock-case?interviewSessionId=${result}`, {
          state: { interviewCase: matchingCase },
        });
      } else {
        setErrorMessage("Interview could not be started. Please try again.");
      }
    } catch (err) {
      console.error("Error starting interview:", err);
      setErrorMessage(
        "An error occurred while starting the interview. Please try again."
      );
    } finally {
      setShowCreditConfirmation(false);
      setPendingCaseId(null);
    }
  };

  const totalPages = Math.ceil(filteredCases.length / rowsPerPage);
  const currentCases = filteredCases.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleShuffle = () => {
    setCases((prev) => shuffleArray(prev));
    setCurrentPage(1);
  };

  const handlePromptClick = (caseId: number) => {
    const caseData = cases.find((c) => c.id === caseId);
    setSelectedCase(caseData ?? null);
    setIsPromptOpen(true);
  };

  const handleActionClick = async (id: number, variant: string) => {
    if (!firebaseUser) {
      navigate("/login");
      return;
    }

    if (variant === "Start") {
      await handleStartMockInterview(id);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Simple topic colors
  const getSimpleTopicStyle = (topicName: string) => {
    // Use only a few simple colors in rotation
    const colors = [
      "bg-blue-100 text-blue-700",
      "bg-green-100 text-green-700",
      "bg-purple-100 text-purple-700",
      "bg-gray-100 text-gray-700",
    ];

    // Use a simple hash function to assign consistent colors to topics
    const hash = topicName
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % colors.length;

    return `px-2 py-0.5 rounded text-xs ${colors[colorIndex]}`;
  };

  if (isError) {
    return (
      <div>
        Error fetching cases:{" "}
        {error instanceof Error ? error.message : "Unknown"}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Error Message Dialog */}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          fullScreen={true}
        />
      )}

      {/* Credit Confirmation Dialog */}
      <CreditConfirmationDialog
        isOpen={showCreditConfirmation}
        onClose={() => {
          setShowCreditConfirmation(false);
          setPendingCaseId(null);
        }}
        onConfirm={handleConfirmStart}
      />

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Select Interview Cases</h1>
      </div>

      <div
        className="p-6 min-h-screen rounded-lg"
        // style={{
        //   background:
        //     "linear-gradient(0 deg, rgba(66, 62, 216, 0.08) 0%, rgba(237, 237, 237, 0.08) 100%)",
        // }}
      >
        {/* Mode Selection */}
        <ModeSwitcher mode={mode} setMode={setMode} />

        {/* Tailwind divider line */}
        <div className="h-[1px] bg-[#E6E8EB] my-4" />

        {/* Filters */}
        <FiltersBar
          filters={filters}
          setFilters={setFilters}
          handleShuffle={handleShuffle}
          businessProblems={cases
            .map((c) => c.caseType)
            .filter((v, i, a) => a.indexOf(v) === i)}
          companies={cases
            .map((c) => c.company)
            .filter((v, i, a) => a.indexOf(v) === i)}
          industries={cases
            .map((c) => c.industry)
            .filter((v, i, a) => a.indexOf(v) === i)}
        />

        {/* Table View */}
        <div
          className={styles.tableContainer}
          // style={{
          //   background:
          //     "linear-gradient(0deg, rgba(66, 62, 216, 0.08) 0%, rgba(237, 237, 237, 0.08) 100%)",
          // }}
        >
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.tableHeaderCell}>Title</th>
                <th className={styles.tableHeaderCell}>Industry</th>
                <th className={styles.tableHeaderCell}>Company Style</th>
                <th className={styles.tableHeaderCell}>Est. Time</th>
                {/* Style column removed
                                <th className={styles.tableHeaderCell}>
                                    Style
                                </th>
                                */}
                <th className={styles.tableHeaderCell}>Difficulty</th>
                <th className={styles.tableHeaderCell}>
                  Business Problem Types
                </th>
                <th className={styles.tableHeaderCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCases.map((interviewCase) => (
                <tr key={interviewCase.id} className={styles.tableRow}>
                  <td className={styles.tableCell}>{interviewCase.title}</td>
                  <td className={styles.tableCell}>{interviewCase.industry}</td>
                  <td className={styles.tableCell}>{interviewCase.company}</td>
                  <td className={styles.tableCell}>
                    {interviewCase.expectedTime}
                  </td>
                  {/* Style column removed
                                    <td className={styles.tableCell}>
                                        {interviewCase.isInterviewerLed
                                            ? "Interviewer-led"
                                            : "Interviewee-led"}
                                    </td>
                                    */}
                  <td className={styles.tableCell}>
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        interviewCase.difficulty === "Easy"
                          ? "bg-emerald-100 text-emerald-700"
                          : interviewCase.difficulty === "Medium"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {interviewCase.difficulty}
                    </span>
                  </td>
                  <td className={styles.tableCell}>
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      <span
                        className={getSimpleTopicStyle(interviewCase.caseType)}
                      >
                        {interviewCase.caseType}
                      </span>
                    </div>
                  </td>
                  <td className={styles.tableCell}>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => handlePromptClick(interviewCase.id)}
                      >
                        Prompt
                      </Button>
                      {userFinishedInterview.includes(interviewCase.id) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() =>
                            handleActionClick(interviewCase.id, "Restart")
                          }
                        >
                          Restart
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="text-xs"
                        onClick={() =>
                          handleActionClick(interviewCase.id, "Start")
                        }
                      >
                        {userOngoingInterview.includes(interviewCase.id)
                          ? "Continue"
                          : "Start"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Case Prompt Dialog */}
        {selectedCase && (
          <CasePromptDialog
            isOpen={isPromptOpen}
            onClose={() => setIsPromptOpen(false)}
            caseData={selectedCase}
          />
        )}

        {/* Table Pagination */}
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            <span className="mr-2 text-sm">Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="ml-4 text-sm">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredCases.length)} of{" "}
              {filteredCases.length}
            </span>
          </div>

          {totalPages > 1 && (
            <div className={styles.paginationControls}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Prev
              </Button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show 5 pages around current page
                const startPage = Math.max(1, currentPage - 2);
                const pageNum = startPage + i;
                if (pageNum <= totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className="min-w-[32px]"
                    >
                      {pageNum}
                    </Button>
                  );
                }
                return null;
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectInterviewCases;
